<script>
    import integrationsService from '@/services/integrations.service';
    import Spinner from '@/components/shared/Spinner';
    import { DxSelectBox } from 'devextreme-vue/select-box';

    export default {
        components: { Spinner, DxSelectBox },
        data() {
            return {
                tabs: ['Tiny Pumper'],
                selectedTab: 'Tiny Pumper',
                sensors: [],
                history: [],
                selectedSensorId: null,
                mapping: null,
                mappingError: null,
                form: {
                    metricName: 'in',
                    value: '',
                    timeUtc: ''
                },
                hourly: {
                    isEnabled: false,
                    lastDeliveredUtc: null,
                    autoUntilUtc: null,
                    durationHours: 12,
                    isSaving: false
                },
                durationOptions: [
                    { value: 6, label: '6 hours' },
                    { value: 8, label: '8 hours' },
                    { value: 12, label: '12 hours' },
                    { value: 24, label: '24 hours' }
                ],
                isLoading: false,
                isSaving: false,
                isLoadingMapping: false,
                isLoadingHistory: false,
                loadError: null,
                saveMessage: null
            };
        },
        computed: {
            selectedSensor() {
                return this.sensors.find(s => s.id == this.selectedSensorId) || null;
            },
            levelconIdDisplay() {
                if (this.mapping?.levelconId) return this.mapping.levelconId;
                return this.selectedSensor?.levelconId || '';
            },
            dataRootDisplay() {
                if (this.mapping && this.mapping.rootCode != null) return this.mapping.rootCode;
                return this.selectedSensor?.rootCode || '';
            },
            valueHint() {
                const metric = (this.form.metricName || 'in').trim().toLowerCase();
                return metric === 'in'
                    ? 'Stored as Val (inches). SenVal is left as 0.'
                    : 'Stored as SenVal. Val is left as 0.';
            },
            modeLabel() {
                return this.hourly.isEnabled ? 'Automatic' : 'Manual';
            }
        },
        methods: {
            toLocalInputValue(date) {
                const pad = n => String(n).padStart(2, '0');
                return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
            },
            apiError(err, fallback) {
                if (typeof err?.data === 'string' && err.data) {
                    return err.data;
                }

                return err?.data?.message || err?.message || fallback;
            },
            isHistorySynced(row) {
                const syncedThrough = this.mapping?.lastReportDateTimeUtc;
                if (!syncedThrough || !row?.timeUtc) return false;
                return new Date(row.timeUtc).getTime() <= new Date(syncedThrough).getTime();
            },
            async loadSensors() {
                this.isLoading = true;
                this.loadError = null;
                try {
                    this.sensors = (await integrationsService.sensors()) || [];
                    if (!this.form.timeUtc) this.form.timeUtc = this.toLocalInputValue(new Date());
                } catch (err) {
                    this.loadError = this.apiError(err, 'Could not load sensors.');
                } finally {
                    this.isLoading = false;
                }
            },
            async onSensorChanged(e) {
                const id = e?.value ?? this.selectedSensorId;
                this.selectedSensorId = id;
                this.saveMessage = null;
                this.mapping = null;
                this.mappingError = null;
                this.hourly = { isEnabled: false, lastDeliveredUtc: null, autoUntilUtc: null, durationHours: 12, isSaving: false };
                if (!id) {
                    await this.loadHistory();
                    return;
                }

                this.isLoadingMapping = true;
                try {
                    this.mapping = await integrationsService.sensorMapping(id);
                    if (this.mapping?.dataName) this.form.metricName = this.mapping.dataName;
                    else if (!this.form.metricName) this.form.metricName = 'in';

                    if (this.mapping?.levelconId) {
                        await this.loadHistory(this.mapping.levelconId);
                        await this.loadHourlyStream();
                    } else {
                        await this.loadHistory();
                    }
                } catch (err) {
                    this.mappingError = this.apiError(err, 'Could not load sensor mapping.');
                    await this.loadHistory();
                } finally {
                    this.isLoadingMapping = false;
                }
            },
            async loadHistory(levelconId) {
                this.isLoadingHistory = true;
                try {
                    this.history = (await integrationsService.history(levelconId || undefined)) || [];
                } catch (err) {
                    this.loadError = this.apiError(err, 'Could not load emulator history.');
                } finally {
                    this.isLoadingHistory = false;
                }
            },
            applyHourlyStream(stream) {
                if (!stream) {
                    this.hourly.isEnabled = false;
                    this.hourly.lastDeliveredUtc = null;
                    this.hourly.autoUntilUtc = null;
                    this.hourly.durationHours = 12;
                    return;
                }

                this.hourly.isEnabled = !!stream.isEnabled;
                this.hourly.lastDeliveredUtc = stream.lastDeliveredUtc || null;
                this.hourly.autoUntilUtc = stream.autoUntilUtc || null;
                const allowed = [6, 8, 12, 24];
                this.hourly.durationHours = allowed.includes(stream.durationHours) ? stream.durationHours : 12;
                if (stream.metricName) this.form.metricName = stream.metricName;
                const metric = (this.form.metricName || 'in').trim().toLowerCase();
                const reading = metric === 'in' ? stream.val : stream.senVal;
                if (reading != null && reading !== '') this.form.value = reading;
            },
            async loadHourlyStream() {
                if (!this.selectedSensorId) return;
                try {
                    const stream = await integrationsService.hourlyStream({ sensorId: this.selectedSensorId });
                    this.applyHourlyStream(stream);
                } catch (err) {
                    this.loadError = this.apiError(err, 'Could not load automatic measurement mode.');
                }
            },
            async saveHourly({ isEnabled }) {
                if (!this.selectedSensorId) {
                    this.saveMessage = 'Select a sensor first.';
                    return;
                }
                if (isEnabled && (this.form.value == null || this.form.value === '')) {
                    this.saveMessage = 'Enter a value before switching to Automatic.';
                    return;
                }

                this.hourly.isSaving = true;
                this.saveMessage = null;
                this.loadError = null;
                try {
                    const stream = await integrationsService.putHourlyStream({
                        sensorId: this.selectedSensorId,
                        metricName: this.form.metricName || 'in',
                        value: this.form.value == null || this.form.value === '' ? undefined : String(this.form.value),
                        isEnabled,
                        durationHours: this.hourly.durationHours || 12
                    });
                    this.applyHourlyStream(stream);
                    this.saveMessage = isEnabled && stream?.isEnabled
                        ? `Automatic mode is on for ${stream.durationHours || this.hourly.durationHours || 12} hours (until ${stream.autoUntilUtc || 'the end of the window'}). Readings stamp near the top of each UTC hour; Automatic turns off after the last delivery.`
                        : isEnabled && stream && !stream.isEnabled
                            ? 'Automatic window ended with this delivery; mode is back to Manual.'
                            : 'Manual mode is on. You can add one-off history rows with an exact timestamp.';
                    const levelconId = this.mapping?.levelconId || this.selectedSensor?.levelconId;
                    await this.loadHistory(levelconId);
                    // Refresh mapping so Synced checkboxes pick up LastReportDateTime after delivery.
                    if (this.selectedSensorId) {
                        try {
                            this.mapping = await integrationsService.sensorMapping(this.selectedSensorId);
                        } catch {
                            /* keep prior mapping */
                        }
                    }
                } catch (err) {
                    this.loadError = this.apiError(err, 'Could not update automatic measurement mode.');
                    await this.loadHourlyStream();
                } finally {
                    this.hourly.isSaving = false;
                }
            },
            async setMode(mode) {
                const wantAutomatic = mode === 'automatic';
                if (wantAutomatic === this.hourly.isEnabled) return;
                await this.saveHourly({ isEnabled: wantAutomatic });
            },
            async updateHourlyValue() {
                await this.saveHourly({ isEnabled: true });
            },
            async submitHistory() {
                if (this.hourly.isEnabled) {
                    this.saveMessage = 'Automatic mode is on. Update the hourly value or switch to Manual before adding a one-off reading.';
                    return;
                }
                if (!this.selectedSensorId) {
                    this.saveMessage = 'Select a sensor first.';
                    return;
                }
                if (!this.form.value && this.form.value !== 0) {
                    this.saveMessage = 'Enter a reading value.';
                    return;
                }

                this.isSaving = true;
                this.saveMessage = null;
                this.loadError = null;
                try {
                    const timeUtc = this.form.timeUtc ? new Date(this.form.timeUtc).toISOString() : new Date().toISOString();
                    await integrationsService.postHistory({
                        sensorId: this.selectedSensorId,
                        metricName: this.form.metricName || 'in',
                        value: String(this.form.value),
                        timeUtc
                    });
                    this.saveMessage = 'History saved (updated if same sensor + timestamp).';
                    const levelconId = this.mapping?.levelconId || this.selectedSensor?.levelconId;
                    await this.loadHistory(levelconId);
                    if (this.selectedSensorId) {
                        try {
                            this.mapping = await integrationsService.sensorMapping(this.selectedSensorId);
                        } catch {
                            /* keep prior mapping */
                        }
                    }
                } catch (err) {
                    this.loadError = this.apiError(err, 'Could not save emulator history.');
                } finally {
                    this.isSaving = false;
                }
            },
            async removeHistory(row) {
                try {
                    await integrationsService.deleteHistory(row.id);
                    const levelconId = this.mapping?.levelconId || this.selectedSensor?.levelconId;
                    await this.loadHistory(levelconId);
                } catch (err) {
                    this.loadError = this.apiError(err, 'Could not delete history row.');
                }
            }
        },
        mounted() {
            this.loadSensors();
            this.loadHistory();
        }
    };
</script>

<template>
    <div class="main-content-header">
        <h3>
            <i class="bi bi-plug"></i>
            Integrations
        </h3>
    </div>
    <div class="main-content-body">
        <Spinner :show="isLoading" text="Loading..." style="padding-top: 15% !important" />
        <template v-if="!isLoading">
            <ul class="nav nav-tabs mb-3">
                <li class="nav-item" v-for="tab in tabs" :key="tab" @click="selectedTab = tab">
                    <a class="nav-link" :class="{ active: tab === selectedTab }" href="javascript:;">{{ tab }}</a>
                </li>
            </ul>

            <div v-if="selectedTab === 'Tiny Pumper'">
                <p class="text-muted small mb-3">
                    Seed Levelcon emulator history for a Tiny Pumper sensor, or switch to Automatic so beta TinyPumper keeps delivering the same value near the top of each UTC hour. Re-saving the same sensor and timestamp updates the existing row. Disabled in Production.
                </p>

                <div v-if="loadError" class="alert alert-danger py-2">{{ loadError }}</div>
                <div v-if="saveMessage" class="alert alert-success py-2">{{ saveMessage }}</div>

                <form class="gsb-form mb-4" @submit.prevent="submitHistory">
                    <div class="row mb-2">
                        <label class="col-sm-2 col-form-label" for="integrations-sensor">Sensor</label>
                        <div class="col-sm-6">
                            <DxSelectBox
                                id="integrations-sensor"
                                v-model:value="selectedSensorId"
                                :items="sensors"
                                display-expr="label"
                                value-expr="id"
                                :search-enabled="true"
                                search-mode="contains"
                                :search-expr="['label', 'name', 'companyName', 'batteryName', 'rootCode']"
                                :search-timeout="200"
                                placeholder="Search sensors…"
                                :show-clear-button="true"
                                @value-changed="onSensorChanged"
                            />
                        </div>
                    </div>

                    <div v-if="isLoadingMapping" class="row mb-2">
                        <div class="col-sm-6 offset-sm-2 text-muted small">Loading sensor mapping…</div>
                    </div>
                    <div v-if="mappingError" class="alert alert-warning py-2">{{ mappingError }}</div>

                    <div v-if="mapping" class="row mb-3">
                        <div class="col-sm-6 offset-sm-2">
                            <div class="border rounded p-2 bg-light small">
                                <div class="fw-semibold mb-1">
                                    Mapping
                                    <span v-if="mapping.isConfigured" class="badge text-bg-success ms-1">Configured</span>
                                    <span v-else class="badge text-bg-warning ms-1">Incomplete</span>
                                </div>
                                <div><span class="text-muted">Company:</span> {{ mapping.companyName }} (#{{ mapping.companyId }})</div>
                                <div><span class="text-muted">Facility:</span> {{ mapping.batteryName }} (#{{ mapping.batteryId }})</div>
                                <div>
                                    <span class="text-muted">Asset:</span>
                                    <template v-if="mapping.targetEntityName">
                                        {{ mapping.targetEntityName }}
                                        <span v-if="mapping.targetEntityTypeName">({{ mapping.targetEntityTypeName }})</span>
                                        <span v-if="mapping.targetEntityId"> #{{ mapping.targetEntityId }}</span>
                                    </template>
                                    <span v-else class="text-warning">Not mapped to an asset</span>
                                </div>
                                <div class="mt-1">
                                    <span class="text-muted">TinyPumper synced through:</span>
                                    <template v-if="mapping.lastReportDateTimeUtc">
                                        {{ mapping.lastReportDateTimeUtc }}
                                    </template>
                                    <span v-else class="text-warning">Never (no LastReportDateTime yet)</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-3" v-if="selectedSensorId">
                        <label class="col-sm-2 col-form-label" id="integrations-mode-label">Mode</label>
                        <div class="col-sm-6">
                            <div
                                class="btn-group w-100 integrations-mode-toggle"
                                role="group"
                                aria-labelledby="integrations-mode-label"
                            >
                                <button
                                    type="button"
                                    class="btn btn-lg"
                                    :class="hourly.isEnabled ? 'btn-primary' : 'btn-outline-secondary'"
                                    :disabled="hourly.isSaving"
                                    @click="setMode('automatic')"
                                >
                                    Automatic
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-lg"
                                    :class="!hourly.isEnabled ? 'btn-primary' : 'btn-outline-secondary'"
                                    :disabled="hourly.isSaving"
                                    @click="setMode('manual')"
                                >
                                    Manual
                                </button>
                            </div>
                            <div class="form-text mt-1">
                                <template v-if="hourly.isEnabled">
                                    Automatic: no timestamp needed — each delivery is stamped at the top of the UTC hour (or as close as the timer gets).
                                    Stops after the last hour in the window
                                    <span v-if="hourly.autoUntilUtc">(until {{ hourly.autoUntilUtc }})</span>.
                                    <span v-if="hourly.lastDeliveredUtc"> Last delivery: {{ hourly.lastDeliveredUtc }}.</span>
                                </template>
                                <template v-else>
                                    Manual: pick an exact <code>timeUtc</code> and add one-off history rows.
                                </template>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-3" v-if="selectedSensorId">
                        <label class="col-sm-2 col-form-label" for="integrations-durationHours">Automatic up to</label>
                        <div class="col-sm-6">
                            <select
                                id="integrations-durationHours"
                                class="form-select form-select-sm"
                                v-model.number="hourly.durationHours"
                                :disabled="hourly.isSaving"
                            >
                                <option v-for="opt in durationOptions" :key="opt.value" :value="opt.value">
                                    {{ opt.label }}
                                </option>
                            </select>
                            <div class="form-text">
                                Default 12 hours. Automatic turns off after the last hourly measurement in that window — it does not run forever.
                            </div>
                        </div>
                    </div>

                    <div class="row mb-2" v-if="selectedSensorId">
                        <label class="col-sm-2 col-form-label">Levelcon Id</label>
                        <div class="col-sm-6">
                            <input class="form-control form-control-sm" :value="levelconIdDisplay" readonly />
                        </div>
                    </div>
                    <div class="row mb-2" v-if="selectedSensorId">
                        <label class="col-sm-2 col-form-label">dataRoot</label>
                        <div class="col-sm-6">
                            <input class="form-control form-control-sm" :value="dataRootDisplay" readonly />
                            <div class="form-text">From Sensors.RootCode (string).</div>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <label class="col-sm-2 col-form-label" for="integrations-metricName">metricName</label>
                        <div class="col-sm-6">
                            <input id="integrations-metricName" class="form-control form-control-sm" v-model="form.metricName" />
                        </div>
                    </div>
                    <div class="row mb-2">
                        <label class="col-sm-2 col-form-label" for="integrations-value">Value</label>
                        <div class="col-sm-6">
                            <input id="integrations-value" class="form-control form-control-sm" v-model="form.value" required />
                            <div class="form-text">{{ valueHint }}</div>
                        </div>
                    </div>
                    <div class="row mb-3" v-if="!hourly.isEnabled">
                        <label class="col-sm-2 col-form-label" for="integrations-timeUtc">timeUtc</label>
                        <div class="col-sm-6">
                            <input
                                id="integrations-timeUtc"
                                type="datetime-local"
                                class="form-control form-control-sm"
                                v-model="form.timeUtc"
                                required
                            />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-6 offset-sm-2">
                            <button
                                v-if="!hourly.isEnabled"
                                type="submit"
                                class="btn btn-primary btn-sm"
                                :disabled="isSaving || !selectedSensorId"
                            >
                                {{ isSaving ? 'Saving…' : 'Add history' }}
                            </button>
                            <button
                                v-else
                                type="button"
                                class="btn btn-outline-primary btn-sm"
                                :disabled="hourly.isSaving || !selectedSensorId || form.value == null || form.value === ''"
                                @click="updateHourlyValue"
                            >
                                {{ hourly.isSaving ? 'Saving…' : 'Update automatic value' }}
                            </button>
                            <span v-if="hourly.isSaving && modeLabel === 'Automatic'" class="text-muted small ms-2">Switching mode…</span>
                        </div>
                    </div>
                </form>

                <div class="integrations-history">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                        <h6 class="mb-0">
                            Emulator history
                            <span v-if="levelconIdDisplay" class="text-muted fw-normal">· Levelcon {{ levelconIdDisplay }}</span>
                            <span v-else class="text-muted fw-normal">· all sensors</span>
                        </h6>
                        <span v-if="isLoadingHistory" class="text-muted small">Loading…</span>
                    </div>
                    <table class="table table-sm table-striped" v-if="history.length">
                        <thead>
                            <tr>
                                <th>Id</th>
                                <th v-if="!selectedSensorId">Levelcon</th>
                                <th>DataRoot</th>
                                <th>Metric</th>
                                <th>Val</th>
                                <th>SenVal</th>
                                <th>TimeUtc</th>
                                <th class="text-center" title="TinyPumper Sensors.LastReportDateTime is at or after this row">
                                    Synced
                                </th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in history" :key="row.id">
                                <td>{{ row.id }}</td>
                                <td v-if="!selectedSensorId">{{ row.levelconId }}</td>
                                <td>{{ row.dataRoot }}</td>
                                <td>{{ row.metricName }}</td>
                                <td>{{ row.val }}</td>
                                <td>{{ row.senVal }}</td>
                                <td>{{ row.timeUtc }}</td>
                                <td class="text-center">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        :checked="isHistorySynced(row)"
                                        disabled
                                        :title="
                                            selectedSensorId
                                                ? isHistorySynced(row)
                                                    ? 'TinyPumper has synced through this reading'
                                                    : 'Not synced yet (or select mapping to evaluate)'
                                                : 'Select a sensor to evaluate Synced against LastReportDateTime'
                                        "
                                        :aria-label="`Synced for history ${row.id}`"
                                    />
                                </td>
                                <td>
                                    <button type="button" class="btn btn-link btn-sm text-danger py-0" @click="removeHistory(row)">
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-else class="text-muted small mb-0">
                        {{ isLoadingHistory ? 'Loading emulator history…' : 'No emulator history saved yet.' }}
                    </p>
                    <p v-if="selectedSensorId && !mapping?.lastReportDateTimeUtc" class="text-muted small mt-1 mb-0">
                        Synced checkboxes stay empty until TinyPumper writes <code>Sensors.LastReportDateTime</code> for this sensor.
                    </p>
                </div>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.integrations-mode-toggle .btn {
    min-height: 3rem;
    font-weight: 600;
}
.integrations-history {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid var(--bs-border-color, #dee2e6);
}
</style>
